/**
 * Shared English date formatting: en-GB words, viewer-zone instants, UTC calendar-only dates
 * (otherwise one stored day can display as the previous day). Invalid values are explicit.
 * Tests inject timeZone; browser callers omit it. Only this module may use Intl directly.
 * Month/weekday tables avoid CLDR differences: runtimes disagree on Sep versus Sept even with
 * the same locale. Intl supplies zone conversion and numeric parts; stable words come from here.
 */

const LOCALE = "en-GB";

/** The month names this app writes, in every runtime and every browser. */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** The weekday names, indexed the way `Date.getUTCDay` counts them, Sunday first. */
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
/** What a value that is not a time renders as, in the reader's language rather than the engine's. */
const UNKNOWN = "Unknown date";

/** A stored calendar day, with no time and so no zone of its own: `2026-09-21`. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

export type DateOptions = {
  /** The zone to read the instant in. Omitted in the browser, where the viewer's own is right. */
  readonly timeZone?: string;
};

/**
 * The instant a value names, and whether it carried its own day.
 *
 * A calendar day is anchored at UTC midnight and reported as such, so every formatter below can
 * read it in UTC rather than each one remembering to.
 */
function instantOf(value: string): { at: Date; calendarDay: boolean } | null {
  const calendarDay = CALENDAR_DAY.test(value);
  const at = new Date(calendarDay ? `${value}T00:00:00Z` : value);
  return Number.isNaN(at.getTime()) ? null : { at, calendarDay };
}

/** The calendar fields an instant has in one zone, as numbers, which every runtime agrees on. */
type Fields = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly weekday: number;
  readonly clock: string;
};

/**
 * The fields of a value in the zone it should be read in, or `null` when it is not a time.
 *
 * Only numeric parts are asked of `Intl`, so nothing here depends on the runtime's CLDR. The
 * weekday is arithmetic on the zone-local day rather than a name `Intl` chose: a day of the week is
 * a property of the date, and asking for it by name would put the drift back.
 */
function fieldsOf(value: string, zone?: string): Fields | null {
  const parsed = instantOf(value);
  if (parsed === null) return null;
  const parts = new Intl.DateTimeFormat(LOCALE, {
    timeZone: parsed.calendarDay ? "UTC" : zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed.at);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  const year = Number(part("year"));
  const month = Number(part("month"));
  const day = Number(part("day"));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return {
    year,
    month,
    day,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
    clock: `${part("hour")}:${part("minute")}`,
  };
}

/** The month's own name, never the runtime's spelling of it. */
const monthName = (fields: Fields): string => MONTHS[fields.month - 1] ?? String(fields.month);

/** A date alone: "21 Sep 2026". */
export const appDate = (value: string, options: DateOptions = {}): string => {
  const fields = fieldsOf(value, options.timeZone);
  return fields === null ? UNKNOWN : `${fields.day} ${monthName(fields)} ${fields.year}`;
};

/** Compose date and clock to keep both spellings consistent and omit Intl's intervening “at”. */
export const appDateTime = (value: string, options: DateOptions = {}): string => {
  const date = appDate(value, options);
  if (date === UNKNOWN) return UNKNOWN;
  // A stored calendar day has no clock; giving it "00:00" would state a time nobody recorded.
  return CALENDAR_DAY.test(value) ? date : `${date}, ${appTime(value, options)}`;
};

/**
 * A date in a mail header, the way mail clients write a forwarded message's: "Thu, 16 Oct 2026 at
 * 14:33". The zone label is the caller's to add, because a header goes to a reader in another zone.
 */
export const appMailDate = (value: string, options: DateOptions = {}): string => {
  const fields = fieldsOf(value, options.timeZone);
  if (fields === null) return UNKNOWN;
  const day = `${WEEKDAYS[fields.weekday] ?? ""}, ${fields.day} ${monthName(fields)} ${fields.year}`;
  return `${day} at ${fields.clock}`;
};

/** A weekday with its clock, for a moment close enough that the day of the week is the useful part. */
export const appWeekdayTime = (value: string, options: DateOptions = {}): string => {
  const day = appWeekdayDayMonth(value, options);
  return day === UNKNOWN ? UNKNOWN : `${day}, ${appTime(value, options)}`;
};

/** A weekday with its date and no clock, for a heading a list groups under: "Mon 21 Sep". */
export const appWeekdayDayMonth = (value: string, options: DateOptions = {}): string => {
  const fields = fieldsOf(value, options.timeZone);
  if (fields === null) return UNKNOWN;
  return `${WEEKDAYS[fields.weekday] ?? ""} ${fields.day} ${monthName(fields)}`.trim();
};

/** The clock alone, for a time whose day the surrounding text already gave: "16:30". */
export const appTime = (value: string, options: DateOptions = {}): string => {
  const fields = fieldsOf(value, options.timeZone);
  return fields === null ? UNKNOWN : fields.clock;
};

/** A day and month, for a row with no space for the year: "21 Sep". */
export const appDayMonth = (value: string, options: DateOptions = {}): string => {
  const fields = fieldsOf(value, options.timeZone);
  return fields === null ? UNKNOWN : `${fields.day} ${monthName(fields)}`;
};

/**
 * The viewer's own zone, for the formatters above and for the activity view's own grouping.
 *
 * This is the one legitimate reading of the browser's settings: the zone is the reader's, the
 * locale is not. It lives here so `Intl` stays in one file and the rule's test can ban the bare
 * constructor everywhere else.
 */
export const viewerTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Every region/city zone this browser knows, for a picker, plus `UTC`. Empty where the runtime
 * cannot list them, which leaves the field a plain text box rather than failing.
 */
export const timeZoneNames = (): readonly string[] => {
  const list =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  return list.includes("UTC") ? list : ["UTC", ...list];
};

/**
 * Whether a typed zone is one the host will accept, by the host's own rule (`timezoneSchema` in
 * context-contracts.ts): `UTC` or a region/city name the runtime knows. "CET" and "+02:00" are
 * refused there, so a form can say so before it posts rather than show the host's refusal.
 */
export const isTimeZone = (value: string): boolean => {
  if (value !== "UTC" && !value.includes("/")) return false;
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

/** What a clock in one zone shows at one instant, in the stored shapes a field holds. */
export type ZoneClock = {
  /** The zone's calendar day: `2026-10-05`. */
  readonly date: string;
  /** The zone's clock: `16:05`. */
  readonly time: string;
  /** How far the zone is ahead of UTC at that instant, in minutes; negative when behind. */
  readonly offsetMinutes: number;
};

const clockFormats = new Map<string, Intl.DateTimeFormat | null>();
/** One formatter per zone: building one costs more than using it, and a picker asks for 400. */
function clockFormat(zone: string): Intl.DateTimeFormat | null {
  const known = clockFormats.get(zone);
  if (known !== undefined) return known;
  let made: Intl.DateTimeFormat | null = null;
  try {
    made = new Intl.DateTimeFormat(LOCALE, {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    made = null;
  }
  clockFormats.set(zone, made);
  return made;
}

const two = (value: number): string => String(value).padStart(2, "0");

/**
 * The clock in a zone at an instant, or `null` for a zone this runtime does not know.
 *
 * Only numbers are asked of `Intl`, as everywhere in this file. The offset is the difference
 * between the zone's own reading and the instant, so it needs no offset name from the runtime and
 * is right for the instant asked about, which is how a field can say "UTC+2" for a slot in October
 * and "UTC+1" for one in November.
 */
export const zoneClock = (zone: string, at: number): ZoneClock | null => {
  const format = zone === "" || !Number.isFinite(at) ? null : clockFormat(zone);
  if (format === null) return null;
  const parts = format.formatToParts(new Date(at));
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((candidate) => candidate.type === type)?.value);
  const [year, month, day, hour, minute] = [
    part("year"),
    part("month"),
    part("day"),
    part("hour"),
    part("minute"),
  ];
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
  const reading = Date.UTC(year, month - 1, day, hour, minute);
  const instant = Math.floor(at / 60_000) * 60_000;
  return {
    date: `${String(year).padStart(4, "0")}-${two(month)}-${two(day)}`,
    time: `${two(hour)}:${two(minute)}`,
    offsetMinutes: (reading - instant) / 60_000,
  };
};

/** An offset as a rep reads it: "UTC+2", "UTC+5:30", "UTC-7", and plain "UTC" for none. */
export const offsetLabel = (minutes: number): string => {
  if (minutes === 0) return "UTC";
  const size = Math.abs(minutes);
  const rest = size % 60;
  return `UTC${minutes < 0 ? "-" : "+"}${Math.floor(size / 60)}${rest === 0 ? "" : `:${two(rest)}`}`;
};

/**
 * What a clock reading in a zone amounts to.
 *
 * `never` is a reading the clocks skip when they go forward, `twice` one they show again when they
 * go back; `from` and `to` are the hour that is missing or repeated, as that zone's clock writes
 * it. The host refuses both (`disambiguation: "reject"`), so a field refuses them first.
 */
export type WallClock =
  | { readonly kind: "once"; readonly at: number; readonly offsetMinutes: number }
  | { readonly kind: "never"; readonly from: string; readonly to: string }
  | { readonly kind: "twice"; readonly from: string; readonly to: string }
  | { readonly kind: "unknown" };

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MINUTE = 60_000;
/** Further than any zone's clock change reaches, either side of the reading. */
const AROUND = 36 * 60 * MINUTE;

/** The instant a stored clock reading (`2026-10-12T14:00`) names in a zone. */
export const resolveWallClock = (local: string, zone: string): WallClock => {
  const match = WALL_CLOCK.exec(local);
  if (!match) return { kind: "unknown" };
  const reading = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );
  // `Date.UTC` rolls 31 February into March; a reading that does not come back is no reading.
  if (new Date(reading).toISOString().slice(0, 16) !== local) return { kind: "unknown" };
  const before = zoneClock(zone, reading - AROUND);
  const after = zoneClock(zone, reading + AROUND);
  if (before === null || after === null) return { kind: "unknown" };
  const instants = [...new Set([before.offsetMinutes, after.offsetMinutes])]
    .map((offset) => reading - offset * MINUTE)
    .filter((instant) => {
      const shown = zoneClock(zone, instant);
      return shown !== null && `${shown.date}T${shown.time}` === local;
    });
  const [only] = instants;
  if (instants.length === 1 && only !== undefined)
    return { kind: "once", at: only, offsetMinutes: (reading - only) / MINUTE };
  if (before.offsetMinutes === after.offsetMinutes) return { kind: "unknown" };
  // The first minute on the new offset, by halving the window the change is known to be in.
  let old = reading - AROUND;
  let changed = reading + AROUND;
  while (changed - old > MINUTE) {
    const middle = old + Math.floor((changed - old) / (2 * MINUTE)) * MINUTE;
    if (zoneClock(zone, middle)?.offsetMinutes === before.offsetMinutes) old = middle;
    else changed = middle;
  }
  const shown = (offset: number): string =>
    new Date(changed + offset * MINUTE).toISOString().slice(11, 16);
  return instants.length === 0
    ? { kind: "never", from: shown(before.offsetMinutes), to: shown(after.offsetMinutes) }
    : { kind: "twice", from: shown(after.offsetMinutes), to: shown(before.offsetMinutes) };
};

const zoneNames = new Map<string, readonly string[]>();
const zoneNamePart = (format: Intl.DateTimeFormat, at: number): string =>
  format.formatToParts(new Date(at)).find((part) => part.type === "timeZoneName")?.value ?? "";

/**
 * The words a zone is also known by: its own name ("Central European Time") first when the runtime
 * has one, then the abbreviations people write beside a time ("CET", "CEST", "EST").
 *
 * These are the one place this file takes words from `Intl`, and the header's warning applies to
 * them: a runtime spells them from its own CLDR. So they are for finding a zone and for a quiet
 * second line under a city, never a value, and no test pins a name. Both seasons are asked for,
 * because a lead writes "EST" in July; "GMT+2" style answers are dropped, the offset says that.
 */
export const zoneWords = (zone: string, at: number): readonly string[] => {
  const year = new Date(at).getUTCFullYear();
  const key = `${zone}|${year}`;
  const known = zoneNames.get(key);
  if (known) return known;
  const words = new Set<string>();
  try {
    try {
      const name = zoneNamePart(
        new Intl.DateTimeFormat(LOCALE, { timeZone: zone, timeZoneName: "longGeneric" }),
        at,
      );
      if (name && !/^(?:GMT|UTC)[+-]/.test(name)) words.add(name);
    } catch {
      // A runtime without generic names still has the abbreviations below.
    }
    for (const locale of [LOCALE, "en-US"]) {
      const short = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "short" });
      for (const month of [0, 6]) {
        const word = zoneNamePart(short, Date.UTC(year, month, 15));
        if (word && !/^(?:GMT|UTC)[+-]/.test(word)) words.add(word);
      }
    }
  } catch {
    return [];
  }
  const found = [...words];
  zoneNames.set(key, found);
  return found;
};

/**
 * A count, for the same reason the dates are pinned: "1 234" under an English heading reads as a
 * different app than "1,234". A number is not a date, but a browser locale renders it just as
 * freely, so it is held to the same rule.
 */
export const appCount = (value: number): string => new Intl.NumberFormat(LOCALE).format(value);
